import '@/lib/dayjs';
import { Box } from '@mui/material';
import { memo, useMemo, useState } from 'react';

import { SectionTitle } from '@/components/shared/Typography/Typography';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import type { AuditLogFilters } from '@/hooks/useAuditLogs';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';

import AuditLogsFilter from './components/AuditLogsFilter';
import AuditLogsTable from './components/AuditLogsTable';

interface AuditLogsListProps {
  title?: string;
  titleTestId?: string;
}

function AuditLogsList({ title = 'Audit', titleTestId }: AuditLogsListProps) {
  const { organizationId } = useOrganizationId();
  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const [appliedFilters, setAppliedFilters] = useState<AuditLogFilters | null>(null);
  const { auditLogs, isLoading, error } = useAuditLogs(apiPage, pagination.pageSize, appliedFilters);

  const auditLogsList = useMemo(() => auditLogs?.items ?? [], [auditLogs]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: 'background.paper',
          py: 1,
        }}
      >
        {title && (
          <SectionTitle data-testid={titleTestId} sx={{ marginBottom: 0 }}>
            {title}
          </SectionTitle>
        )}
        <AuditLogsFilter
          organizationId={organizationId ?? null}
          appliedFilters={appliedFilters}
          onChange={(filters) => {
            setAppliedFilters(filters);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
        />
      </Box>
      <AuditLogsTable
        auditLogs={auditLogsList}
        pagination={pagination}
        totalCount={auditLogs?.totalCount ?? 0}
        isLoading={isLoading}
        error={error as Error | null | undefined}
        onPaginationChange={setPagination}
      />
    </Box>
  );
}

export default memo(AuditLogsList);
